import React from "react";
import { Button, Card, Space, Tag, Typography } from "antd";
import { LineChartOutlined, LockOutlined } from "@ant-design/icons";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

const { Title, Paragraph, Text } = Typography;

interface Props {
    onUpgradeClick?: () => void;
}

export const LockedSegmentComparisonPreview: React.FC<Props> = ({ onUpgradeClick }) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const handleUpgrade = onUpgradeClick ?? (() => navigate("/dashboard/organisation?tab=billing#upgrade"));
    return (
        <div style={{ padding: 32, display: "flex", justifyContent: "center" }}>
            <Card
                style={{ maxWidth: 720, width: "100%", textAlign: "center" }}
                styles={{ body: { padding: "40px 48px" } }}
            >
                <Space direction="vertical" size="large" style={{ width: "100%" }}>
                    <div>
                        <LockOutlined style={{ fontSize: 36, color: "var(--link-color, #ff825c)" }} />
                    </div>
                    <Tag color="orange" style={{ fontWeight: 600, letterSpacing: 0.6 }}>
                        {intl.formatMessage({
                            id: "segmentComparison.locked.tag",
                            defaultMessage: "PROFESSIONAL FEATURE",
                        })}
                    </Tag>
                    <Title level={3} style={{ margin: 0 }}>
                        {intl.formatMessage({
                            id: "segmentComparison.locked.title",
                            defaultMessage: "Compare audiences side by side on Trends",
                        })}
                    </Title>
                    <Paragraph type="secondary" style={{ margin: 0 }}>
                        {intl.formatMessage({
                            id: "segmentComparison.locked.body",
                            defaultMessage:
                                "Overlay multiple customer segments — by cohort, country, or any custom attribute — on this brand's BE trend chart to see how different audiences diverge over time.",
                        })}
                    </Paragraph>

                    {/* Mini preview: 3 stylised lines on a faux chart */}
                    <div
                        style={{
                            background: "#fafafa",
                            border: "1px solid #f0f0f0",
                            borderRadius: 8,
                            padding: 16,
                        }}
                    >
                        <svg viewBox="0 0 600 200" style={{ width: "100%", height: 180 }}>
                            <line x1="40" y1="180" x2="580" y2="180" stroke="#d9d9d9" />
                            <line x1="40" y1="20" x2="40" y2="180" stroke="#d9d9d9" />
                            {[40, 80, 120, 160].map((y) => (
                                <line
                                    key={y}
                                    x1="40"
                                    y1={y}
                                    x2="580"
                                    y2={y}
                                    stroke="#f0f0f0"
                                />
                            ))}
                            <path
                                d="M 40 100 L 130 95 L 220 88 L 310 80 L 400 72 L 490 65 L 580 60"
                                stroke="#1677ff"
                                strokeWidth="2.5"
                                fill="none"
                            />
                            <path
                                d="M 40 70 L 130 62 L 220 55 L 310 50 L 400 44 L 490 38 L 580 32"
                                stroke="#ff825c"
                                strokeWidth="2.5"
                                fill="none"
                            />
                            <path
                                d="M 40 140 L 130 138 L 220 132 L 310 130 L 400 125 L 490 122 L 580 118"
                                stroke="#52c41a"
                                strokeWidth="2.5"
                                fill="none"
                            />
                        </svg>
                        <Space size={18} style={{ marginTop: 8 }}>
                            <Space size={6}>
                                <span style={{ display: "inline-block", width: 14, height: 3, background: "#1677ff" }} />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {intl.formatMessage({ id: "segmentComparison.locked.mockLegend.allResponses", defaultMessage: "All responses" })}
                                </Text>
                            </Space>
                            <Space size={6}>
                                <span style={{ display: "inline-block", width: 14, height: 3, background: "#ff825c" }} />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {intl.formatMessage({ id: "segmentComparison.locked.mockLegend.usCustomers", defaultMessage: "US customers" })}
                                </Text>
                            </Space>
                            <Space size={6}>
                                <span style={{ display: "inline-block", width: 14, height: 3, background: "#52c41a" }} />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {intl.formatMessage({ id: "segmentComparison.locked.mockLegend.ukCustomers", defaultMessage: "UK customers" })}
                                </Text>
                            </Space>
                        </Space>
                    </div>

                    <Space direction="vertical" size={4} style={{ textAlign: "left" }}>
                        <Text>{intl.formatMessage({ id: "segmentComparison.locked.bullet1", defaultMessage: "Up to 3 segments (Professional) or 6 (Enterprise)" })}</Text>
                        <Text>{intl.formatMessage({ id: "segmentComparison.locked.bullet2", defaultMessage: "Confidence-interval bands and statistical guardrails" })}</Text>
                        <Text>{intl.formatMessage({ id: "segmentComparison.locked.bullet3", defaultMessage: "Compare any date range against another (Google Analytics style)" })}</Text>
                    </Space>

                    <Button
                        type="primary"
                        size="large"
                        icon={<LineChartOutlined />}
                        onClick={handleUpgrade}
                    >
                        {intl.formatMessage({ id: "segmentComparison.locked.cta", defaultMessage: "Upgrade plan" })}
                    </Button>
                </Space>
            </Card>
        </div>
    );
};

export default LockedSegmentComparisonPreview;
